import styled from '@emotion/styled';
import { CustomTheme } from '../../../../context/PermissionsContext/ThemeContext/themesTypes';

// Form container components
export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
  width: 100%;
  overflow: visible;
`;

export const InputBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 16px;
`;

export const DateContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;

  & > div {
    flex: 1;
  }
`;

// Input components
export const CustomInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  position: relative;
`;

export const CustomInputLabel = styled.div<{ theme?: CustomTheme }>`
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Manrope;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  margin-bottom: 12px;
  color: ${({ theme }) => theme?.custom?.text || '#333'};
`;

export const TextInputCustom = styled.input<{ theme?: CustomTheme }>`
  padding: 14px 12px;
  font-family: Manrope;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  color: ${({ theme }) => theme?.custom?.text || '#333'};
  font-feature-settings: 'liga' off, 'clig' off;
  border: 1px solid
    ${({ theme }) => theme?.stroke || 'var(--fi-stroke, #d7d9d6)'};
  outline: 0;
  border-radius: 6px;
  background-color: ${({ theme }) => theme?.custom?.background2 || '#ffffff'};

  &:focus {
    border-color: ${({ theme }) =>
      theme?.primary || 'var(--fi-accent, #FF6A00)'};
  }

  &::placeholder {
    color: ${({ theme }) => theme?.custom?.textLight || '#858585'};
    opacity: 1;
  }
`;

// Item selection components
export const ItemsLabel = styled.div<{ theme?: CustomTheme }>`
  font-family: Manrope;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  color: ${({ theme }) => theme?.custom?.text || '#333'};
`;

export const Items = styled.div<{ numColumns: number }>`
  display: grid;
  grid-template-columns: ${({ numColumns }) => `repeat(${numColumns}, 1fr)`};
  gap: 10px;
`;

export const Item = styled.div<{ isSelected: boolean; theme?: CustomTheme }>`
  width: 100%;
  background-color: ${({ isSelected, theme }) =>
    isSelected
      ? theme?.primaryLight || 'var(--fi-accent-soft, #FAC8B2)'
      : theme?.custom?.background1 || '#fff'};
  color: ${({ isSelected, theme }) =>
    isSelected
      ? theme?.primary || 'var(--fi-accent, #FF6A00)'
      : theme?.custom?.text || '#222428'};
  border: ${({ isSelected, theme }) =>
    isSelected
      ? `1px solid ${theme?.primaryDark || 'var(--fi-accent-strong, #DC5C01)'}`
      : `1px solid ${theme?.stroke || 'var(--fi-stroke, #d7d9d6)'}`};
  text-align: center;
  font-family: Manrope;
  font-size: 12px;
  font-weight: 700;
  padding: 14px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ isSelected, theme }) =>
      isSelected
        ? theme?.primaryDark || 'var(--fi-accent-strong, #DC5C01)'
        : theme?.primary || 'var(--fi-accent, #FF6A00)'};
  }
`;

export const ItemContent = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: inherit;
`;

// Date picker components
export const EmptyDateValueContainer = styled.div<{
  theme?: CustomTheme;
  hasError?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  align-items: center;
  border-radius: 8px;
  background-color: ${({ theme }) => theme?.custom?.background2 || '#fff'};
  padding: 16px;
  padding-right: 12px;
  height: 56px;
  width: 100%;
  border: 1px solid
    ${({ hasError, theme }) =>
      hasError ? '#FF5C77' : theme?.stroke || 'var(--fi-stroke, #d7d9d6)'};
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ hasError, theme }) =>
      hasError ? '#FF5C77' : theme?.primary || 'var(--fi-accent, #FF6A00)'};
  }
`;

export const EmptyDateValueText = styled.div<{ theme?: CustomTheme }>`
  font-weight: 600;
  font-family: Manrope;
  font-size: 15px;
  color: ${({ theme }) => theme?.custom?.text || '#333'};
`;

export const EmptyDateValueIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 8px;
`;

// Select components
export const CustomSelectWrapper = styled.select<{ theme?: CustomTheme }>`
  border: 1px solid ${({ theme }) => theme?.custom?.stroke || '#e0e0e0'};
  border-radius: 8px;
  padding: 14px 16px !important;
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  background-image: url('/images/chevron-down.svg');
  background-size: 16px;
  background-repeat: no-repeat;
  background-position: calc(100% - 16px) center;
  background-color: ${({ theme }) => theme?.custom?.background1 || 'white'};
  cursor: pointer;
  font-family: Manrope;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
  color: ${({ theme }) => theme?.custom?.text || '#333'};
  font-feature-settings: 'liga' off, 'clig' off;
  transition: border-color 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: ${({ theme }) =>
      theme?.primary || 'var(--fi-accent, #FF6A00)'};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) =>
      theme?.primary || 'var(--fi-accent, #FF6A00)'};
  }
`;

// Error and validation components
export const ErrorMessage = styled.div`
  color: #ff5c77;
  font-size: 14px;
  margin-top: 10px;
  font-weight: 700;
`;

// Text area components
export const CustomTextArea = styled.textarea<{
  theme?: CustomTheme;
  hasError?: boolean;
}>`
  border: 1px solid
    ${({ hasError, theme }) =>
      hasError ? '#FF5C77' : theme?.stroke || 'var(--fi-stroke, #d7d9d6)'};
  outline: none;
  resize: none;
  box-sizing: border-box;
  width: 100%;
  height: 120px;
  padding: 20px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme?.custom?.background2 || '#ffffff'};
  font-family: Manrope;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;
  color: ${({ theme }) => theme?.custom?.text || '#333'};
  font-feature-settings: 'liga' off, 'clig' off;

  &:focus {
    border-color: ${({ hasError, theme }) =>
      hasError ? '#FF5C77' : theme?.primary || 'var(--fi-accent, #FF6A00)'};
  }

  &::placeholder {
    color: ${({ theme }) => theme?.custom?.textLight || '#858585'};
    opacity: 0.7;
  }
`;

// Section container components
export const SectionContainer = styled.div<{ theme?: CustomTheme }>`
  background-color: ${({ theme }) => theme?.custom?.background2 || '#fff'};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid
    ${({ theme }) => theme?.stroke || 'var(--fi-stroke, #e0e0e0)'};
`;

export const SectionTitle = styled.div<{ theme?: CustomTheme }>`
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 16px;
  color: ${({ theme }) => theme?.custom?.text || '#333'};
`;

// Button components
export const ActionButton = styled.button<{
  variant?: 'primary' | 'secondary';
  theme?: CustomTheme;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  background-color: ${({ variant, theme }) =>
    variant === 'primary'
      ? theme?.primary || 'var(--fi-button, #FF6A00)'
      : theme?.custom?.background2 || '#ffffff'};
  color: ${({ variant, theme }) =>
    variant === 'primary'
      ? theme?.custom?.textCTA || '#ffffff'
      : theme?.primary || 'var(--fi-accent, #FF6A00)'};
  min-width: 110px;
  border: ${({ variant, theme }) =>
    variant === 'primary'
      ? 'none'
      : `1px solid ${theme?.primary || 'var(--fi-accent, #FF6A00)'}`};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    background-color: ${({ variant, theme }) =>
      variant === 'primary'
        ? theme?.stroke || 'var(--fi-stroke, #e0e0e0)'
        : theme?.custom?.background2 || '#ffffff'};
    color: ${({ variant, theme }) =>
      variant === 'primary'
        ? theme?.custom?.textLight || '#999'
        : theme?.custom?.textLight || '#cccccc'};
    border-color: ${({ variant, theme }) =>
      variant === 'primary'
        ? 'transparent'
        : theme?.custom?.textLight || '#cccccc'};
    cursor: not-allowed;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

export const ButtonGroup = styled.div<{
  theme?: CustomTheme;
  isDark?: boolean;
}>`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding: 16px 24px;
  border-top: 1px solid
    ${({ theme, isDark }) =>
      isDark
        ? theme?.stroke || 'var(--fi-stroke, #404040)'
        : theme?.stroke || 'var(--fi-stroke, #e0e0e0)'};
  background-color: ${({ theme, isDark }) =>
    isDark
      ? theme?.custom?.background1 || '#121212'
      : theme?.custom?.background2 || '#ffffff'};
`;

// Container for rules and rewards
export const RulesAndRewardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Contestants

// Styled components for the new UI design
export const ContestantsPanel = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  background-color: ${({ theme }) => theme?.custom?.background2 || 'white'};
  padding: 16px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme }) => theme?.stroke || 'var(--fi-stroke, #e0e0e0)'};
`;

export const ContestantPill = styled.div<{
  backgroundColor?: string;
  theme?: CustomTheme;
}>`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme?.custom?.primary || '#F1F3F7'};
  border-radius: 8px;
  padding: 6px 12px;
  gap: 8px;
  cursor: pointer;
`;

export const ContestantId = styled.div<{ theme?: CustomTheme }>`
  font-size: 13px;
  color: ${({ theme }) => theme?.custom?.text || '#333'};
`;

export const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #666;
  background-color: #e0e0e0;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  &:hover {
    color: var(--fi-accent, #ff6a00);
    background-color: #eeeeee;
  }
`;

export const AddButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  gap: 4px;
`;
